/**
 * MergeCustomerModal (MERGE-360, Seth 2026-08-17) — merge THIS account (the
 * duplicate) into a chosen primary. What actually happens (all real, all
 * audited, all reversible):
 *   - every Pegasus-native record moves to the primary (dispatches, invoices,
 *     payments, messages, files, notes, equipment, properties…)
 *   - do-not-contact restrictions carry over (never lost)
 *   - this account hides from search + its 360 redirects to the primary
 *   - this account's portal links die
 *   - a manifest records every moved row → Undo merge restores it
 * ESC history (invoices/dispatches in the mirror) consolidates at CUTOVER —
 * the merge ledger is that migration's worklist. The office sees both eras
 * meanwhile via the property-history cross-links.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, GitMerge, MapPin, Search, UserRound } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import {
  useCustomerSearch, useMergePreview, useMergeCustomer,
  type CustomerWithLocations,
} from "./api";

const TABLE_LABEL: Record<string, string> = {
  pegasus_dispatch: "Service calls & tickets",
  pegasus_job: "Jobs",
  pegasus_job_invoice: "Invoices (Pegasus)",
  pegasus_quote: "Proposals",
  pegasus_plan_sale: "Membership sales",
  pegasus_plan_accessory: "Plan accessories",
  pegasus_agreement_accessory: "Agreement accessories",
  pegasus_payment: "Payments",
  pegasus_followup: "Follow-ups",
  pegasus_note: "Notes",
  pegasus_file: "Files & photos",
  pegasus_equipment: "Equipment (Pegasus-added)",
  pegasus_equipment_override: "Equipment corrections",
  pegasus_contact_link: "Contacts",
  pegasus_customer_email: "Saved emails",
  pegasus_collection_note: "Collection notes",
  pegasus_appointment_action: "Appointment confirmations",
  pegasus_recommendation: "Recommendations",
  pegasus_outbox: "Queued/held messages",
  pegasus_po: "Purchase orders",
  pegasus_geocode_quarantine: "Geocode fixes",
  pegasus_property: "Properties & ownership",
  message: "Messages (texts, emails, calls)",
  extracted_signal: "Access & preference signals",
};

export function MergeCustomerModal({ customer, onClose }: { customer: CustomerWithLocations; onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [primary, setPrimary] = useState<CustomerWithLocations | null>(null);
  const [reason, setReason] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query), 300);
    return () => window.clearTimeout(t);
  }, [query]);
  useEffect(() => inputRef.current?.focus(), []);

  const { data: hits } = useCustomerSearch(debounced);
  const candidates = useMemo(
    () => (hits ?? []).filter((c) => c.custno !== customer.custno).slice(0, 8),
    [hits, customer.custno],
  );
  // Preview what moves — counts come from the same list the merge RPC uses.
  const { data: preview } = useMergePreview(primary ? customer.custno : null);
  const merge = useMergeCustomer();
  const totalRows = Object.values(preview ?? {}).reduce((s, n) => s + n, 0);

  const doMerge = async () => {
    if (!primary || !reason.trim()) return;
    await merge.mutateAsync({ duplicate: customer.custno, primary: primary.custno, reason: reason.trim() });
    onClose();
    navigate(`/customers/${primary.custno}`);
  };

  return (
    <Modal open onClose={onClose} size="lg" icon={<GitMerge className="h-4 w-4 text-brand-500" />}
      title="Merge this account into another"
      subtitle={`${customer.fullname ?? customer.custno} (#${customer.custno}) is the DUPLICATE — pick the account that stays`}>
      {!primary ? (
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find the account to KEEP — name, address, phone, or customer #…"
              className="w-full rounded-lg border border-hairline py-2.5 pl-9 pr-3 text-[15px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <div className="max-h-72 overflow-auto rounded-lg border border-hairline">
            {debounced.trim().length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">Search for the account everything should move into.</div>
            ) : candidates.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No matches.</div>
            ) : (
              <div className="divide-y divide-hairline">
                {candidates.map((c) => (
                  <button key={c.custno} type="button" onClick={() => setPrimary(c)}
                    className="group flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-brand-50/60">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 group-hover:bg-brand-100 group-hover:text-brand-700">
                      <UserRound className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span className="truncate text-sm font-semibold text-slate-900">{c.fullname ?? c.custno}</span>
                        <span className="shrink-0 text-[11px] text-slate-400">#{c.custno}</span>
                      </span>
                      {(c.locations[0]?.add1 || c.add1) && (
                        <span className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="h-3 w-3 text-slate-400" />
                          {[c.locations[0]?.add1 ?? c.add1, c.locations[0]?.city ?? c.city].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-brand-500" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* The direction, unmistakably. */}
          <div className="flex items-center justify-center gap-3 rounded-xl border border-hairline bg-slate-50 px-4 py-3 text-sm">
            <span className="min-w-0 text-center">
              <span className="block truncate font-semibold text-slate-500 line-through">{customer.fullname ?? customer.custno}</span>
              <span className="block text-[11px] text-slate-400">#{customer.custno} · duplicate, will be hidden</span>
            </span>
            <ArrowRight className="h-5 w-5 shrink-0 text-brand-500" />
            <span className="min-w-0 text-center">
              <span className="block truncate font-semibold text-emerald-700">{primary.fullname ?? primary.custno}</span>
              <span className="block text-[11px] text-slate-400">#{primary.custno} · keeps everything</span>
            </span>
          </div>

          <div className="rounded-lg border border-hairline bg-white px-4 py-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Moving to {primary.fullname ?? primary.custno}
            </div>
            {preview === undefined ? (
              <div className="text-sm text-slate-400">Counting records…</div>
            ) : totalRows === 0 ? (
              <div className="text-sm text-slate-500">
                No Pegasus records on the duplicate — the merge just hides it and points it at the keeper.
              </div>
            ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700">
                {Object.entries(preview).map(([t, n]) => (
                  <span key={t}><b>{n}</b> {TABLE_LABEL[t] ?? t}</span>
                ))}
              </div>
            )}
            <div className="mt-2 border-t border-hairline pt-2 text-[11px] leading-relaxed text-slate-400">
              Also: do-not-contact settings carry over · this account's portal links are revoked · ESC
              history stays visible on both eras and consolidates at cutover · fully reversible with Undo merge.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Reason (required)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`e.g. duplicate of #${primary.custno} — same homeowner entered twice`}
              className="w-full rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>

          {merge.isError && (
            <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {(merge.error as Error)?.message ?? "Merge failed"}
            </div>
          )}

          <div className="flex justify-between gap-2 pt-1">
            <Button variant="secondary" size="sm" onClick={() => setPrimary(null)}>Pick a different account</Button>
            <div className={cn("flex gap-2")}>
              <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={!reason.trim()} loading={merge.isPending} onClick={doMerge}>
                <GitMerge className="h-3.5 w-3.5" /> Merge into #{primary.custno}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
